import { isTabAllowed } from '@revelio/auth';
import { Tab } from '@revelio/data-access';

export type GlobalAssistantAccessDecision =
  | { allowed: true }
  | {
      allowed: false;
      featureLabel: string;
      message: string;
    };

const INACCESSIBLE_MESSAGE = (featureLabel: string) =>
  `You do not have access to ${featureLabel}. Reach out to your Revelio representative for access.`;

const ROUTE_ACCESS_BY_SEGMENT: Record<
  string,
  { tab: Tab; featureLabel: string }
> = {
  compositions: { tab: Tab.Overview, featureLabel: 'Compositions' },
  'sentiment-ratings': { tab: Tab.Sentiment, featureLabel: 'Sentiment' },
  sentiment: { tab: Tab.Sentiment, featureLabel: 'Sentiment' },
  postings: { tab: Tab.Posting, featureLabel: 'Job Postings' },
  pay: { tab: Tab.Compensation, featureLabel: 'Pay' },
  screener: { tab: Tab.Screener, featureLabel: 'Screener' },
  summary: { tab: Tab.EntitySummary, featureLabel: 'Summary' },
  transitions: { tab: Tab.Transition, featureLabel: 'Transitions' },
};

function deny(featureLabel: string): GlobalAssistantAccessDecision {
  return {
    allowed: false,
    featureLabel,
    message: INACCESSIBLE_MESSAGE(featureLabel),
  };
}

export function getGlobalAssistantRouteAccess(
  navigatePath: string,
  userTabs: Tab[] = []
): GlobalAssistantAccessDecision {
  const segments = navigatePath.split('/').filter(Boolean);
  const [firstSegment, secondSegment] = segments;

  if (segments.length === 1 && firstSegment === 'talent-discovery') {
    return isTabAllowed(Tab.TalentDiscovery, userTabs)
      ? { allowed: true }
      : deny('Individual Discovery');
  }

  const routeAccess =
    secondSegment === undefined
      ? undefined
      : ROUTE_ACCESS_BY_SEGMENT[secondSegment];

  if (!routeAccess) {
    return { allowed: true };
  }

  return isTabAllowed(routeAccess.tab, userTabs)
    ? { allowed: true }
    : deny(routeAccess.featureLabel);
}

export function getGlobalAssistantCustomDashAccess(
  customDashEnabled: boolean
): GlobalAssistantAccessDecision {
  return customDashEnabled ? { allowed: true } : deny('Custom Dash');
}

/** Entity-scoped tabs whose standard pages live under `/${entity}/${segment}`. */
const ENTITY_SCOPED_NAVIGATE_ENTITIES = [
  'company',
  'geography',
  'role',
] as const;

/**
 * Standalone Individual Discovery option id (entity `app`, segment
 * `talent-discovery`) as produced by the backend canonical page intents
 * (`__std__:${entity}:${pathSegment}`). Kept in sync with
 * `getStandardPageMatchForOptionId` / `revelio-canonical-page-intents.ts`.
 */
const TALENT_DISCOVERY_NAVIGATE_OPTION_ID = '__std__:app:talent-discovery';

/**
 * Compute the `__std__:` navigate option ids the account CANNOT access, so the
 * backend can strip them from the `navigate` tool's candidate enum and the
 * "pages you can open" grid before the model ever sees them (FE-2117).
 *
 * Default-allow: an id is included ONLY when
 * {@link getGlobalAssistantRouteAccess} confirms the route is denied. Ungated
 * segments (no `ROUTE_ACCESS_BY_SEGMENT` entry) are never denied. Over-inclusion
 * of a non-existent entity/segment combo is harmless — the backend removes by
 * exact id match — so we cover every gated entity × segment pair the backend
 * might offer.
 */
export function getDeniedStandardNavigateOptionIds(
  userTabs: Tab[] = []
): string[] {
  const denied: string[] = [];

  for (const entity of ENTITY_SCOPED_NAVIGATE_ENTITIES) {
    for (const segment of Object.keys(ROUTE_ACCESS_BY_SEGMENT)) {
      const access = getGlobalAssistantRouteAccess(
        `/${entity}/${segment}`,
        userTabs
      );
      if (!access.allowed) {
        denied.push(`__std__:${entity}:${segment}`);
      }
    }
  }

  if (!getGlobalAssistantRouteAccess('/talent-discovery', userTabs).allowed) {
    denied.push(TALENT_DISCOVERY_NAVIGATE_OPTION_ID);
  }

  return denied;
}
